import React, { useEffect, useState } from 'react';
import { FACEBOOK_APP_ID, API_STATUS } from '../../constants';
import { ExchangeTokenApi } from '../../apis/posts/exchange_token';

export default function FacebookLogin({ facebookLoginStatus }) {
    const [user, setUser] = useState(null);
    const [isSdkReady, setIsSdkReady] = useState(false);
    const [exchangeTokenStatus, setExchangeTokenStatus] = useState(API_STATUS);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        // If SDK is already loaded on window, mark ready immediately
        if (window.FB) {
            setIsSdkReady(true);
            return;
        }

        // 1. Define initialization callback
        window.fbAsyncInit = function () {
            window.FB.init({
                appId: FACEBOOK_APP_ID,
                cookie: true,
                xfbml: true,
                version: 'v19.0',
            });
            setIsSdkReady(true); // Mark SDK ready once init completes
        };

        // 2. Dynamically load Facebook SDK script
        (function (d, s, id) {
            if (d.getElementById(id)) return;
            const js = d.createElement(s);
            js.id = id;
            js.src = 'https://connect.facebook.net/en_US/sdk.js';
            d.getElementsByTagName(s)[0].parentNode.appendChild(js);
        })(document, 'script', 'facebook-jssdk');
    }, []);

    const handleFacebookLogin = () => {
        setErrorMessage("");

        if (!window.FB || !isSdkReady) {
            setErrorMessage("Facebook SDK is still loading. Please wait a moment and try again.");
            return;
        }

        window.FB.login(
            (response) => {
                if (response.authResponse) {
                    const accessToken = response.authResponse.accessToken;
                    console.log('Access Token:', accessToken);
                    ExchangeTokenApi({ fb_token: accessToken, platform: 'facebook' }, setExchangeTokenStatus);
                } else {
                    console.log('User cancelled login or did not fully authorize.');
                    setErrorMessage("Login cancelled or permissions were not granted.");
                }
            },
            { scope: 'public_profile,email,user_photos,user_videos' }
        );
    };

    // Handle token exchange response
    useEffect(() => {
        if (!exchangeTokenStatus.isLoader) {
            if (exchangeTokenStatus.isError) {
                setErrorMessage("Authentication failed due to a network error. Please try again.");
            } else if (exchangeTokenStatus.data) {
                const res = exchangeTokenStatus.data;
                if (res.status === 1) {
                    facebookLoginStatus(true);
                } else {
                    setErrorMessage(res.message || "Failed to authenticate with Facebook.");
                }
            }
        }
    }, [exchangeTokenStatus]);

    return (
        <div className="flex flex-col items-center justify-center py-6 gap-4">
            {/* Loading Spinner during API call or SDK initialization */}
            {(exchangeTokenStatus.isLoader || !isSdkReady) && !errorMessage && (
                <div className="flex flex-col items-center gap-3">
                    <div className="relative w-10 h-10">
                        <div className="w-10 h-10 rounded-full border-4 border-slate-200 dark:border-slate-700"></div>
                        <div className="absolute top-0 left-0 w-10 h-10 rounded-full border-4 border-blue-600 border-t-transparent animate-spin"></div>
                    </div>
                    <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                        {!isSdkReady ? "Loading Facebook SDK..." : "Authenticating with Facebook..."}
                    </span>
                </div>
            )}

            {/* Error Message & Retry Option */}
            {errorMessage && !exchangeTokenStatus.isLoader && (
                <div className="w-full max-w-sm p-4 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800/60 text-center">
                    <p className="text-sm text-red-700 dark:text-red-300 mb-3 font-medium">
                        {errorMessage}
                    </p>
                    <button
                        onClick={handleFacebookLogin}
                        disabled={!isSdkReady}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-md shadow-sm transition-colors disabled:opacity-50"
                    >
                        Try Facebook Login Again
                    </button>
                </div>
            )}

            {/* Ready State - Login Button */}
            {isSdkReady && !exchangeTokenStatus.isLoader && !errorMessage && (
                <button
                    onClick={handleFacebookLogin}
                    className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm rounded-lg shadow-sm transition-all"
                >
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                    <span>Connect Facebook Account</span>
                </button>
            )}
        </div>
    );
}